import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Title,
  SimpleGrid,
  Skeleton,
  Button,
  Group,
  Stack,
  Center,
} from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import * as Icons from "@tabler/icons-react";

import { API_BASE_URL } from "../services/api";
import ProductCard from "../components/ProductCard";
import apiService from "../services/api";
import { useTranslation } from "../context/TranslationContext";

function BannerCard({ source, mimetype, title, targetUrl, buttonText, t }) {
  const isVideo = mimetype?.startsWith("video");

  return (
    <div
      style={{
        display: "block",
        width: "100%",
        aspectRatio: "16 / 9",
        position: "relative",
        overflow: "hidden",
        borderRadius: "8px",
        boxShadow:
          "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
      }}
    >
      {isVideo ? (
        <video
          src={source}
          autoPlay
          muted
          loop
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <img
          src={source}
          alt={title || ""}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}

      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1.5rem",
          zIndex: 1,
        }}
      >
        <div>
          {title ? (
            <Title order={3} c="white" fw={900} lh={1.2} ta="center">
              {title}
            </Title>
          ) : null}
        </div>

        {targetUrl ? (
          <Button
            component={Link}
            to={targetUrl}
            variant="white"
            color="dark"
            style={{
              color: "black",
              backgroundColor: "gold",
            }}
            radius="xl"
          >
            {buttonText || t("shopNow", "Shop Now")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBanner, setActiveBanner] = useState(null);

  useEffect(() => {
    async function fetchProducts() {
      try {
        const banner = await apiService.getActiveLandingPage();
        setActiveBanner(banner);
      } catch (err) {
        console.error("Banner fetch failed", err);
      }

      setLoading(true);
      try {
        const featuredSkus = await apiService.getFeaturedProducts();
        const all = await apiService.getProducts({ lang: t.langCode });

        // Map each featured SKU to the matching variant inside its parent product,
        // then wrap it into a single-variant card so ProductCard renders exactly that variant
        const variantBySku = new Map();
        for (const parent of all) {
          for (const variant of parent.variants || []) {
            variantBySku.set(variant.sku, { parent, variant });
          }
        }

        const featured = featuredSkus
          .map((sku) => variantBySku.get(sku))
          .filter(Boolean)
          .map(({ parent, variant }) => ({
            id: variant.sku,
            product_code: parent.product_code,
            category: parent.category,
            is_bulk_only: parent.is_bulk_only,
            is_variable_weight: parent.is_variable_weight,
            is_hidden: parent.is_hidden,
            image: variant.image,
            images: variant.images,
            allImages: variant.allImages,
            price: variant.price,
            offer: variant.offer,
            translations: variant.translations,
            variants: [variant],
          }));

        const getTotalStock = (p) =>
          (p.variants || []).reduce((sum, v) => sum + (Number(v.stock_quantity) || 0), 0);
        const sorted = featured.slice().sort((a, b) => {
          const aStock = getTotalStock(a);
          const bStock = getTotalStock(b);
          if (aStock === 0 && bStock > 0) return 1;
          if (aStock > 0 && bStock === 0) return -1;
          return 0;
        });
        setProducts(sorted);
      } catch (err) {
        console.error("Product fetch failed", err);
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, [t.langCode]);

  // Build banner slides from the active landing page (if any)
  let bannerSlides = [];
  if (activeBanner) {
    let contentConfig = {};
    try {
      contentConfig =
        typeof activeBanner.content_config === "string"
          ? JSON.parse(activeBanner.content_config)
          : activeBanner.content_config || {};
    } catch (e) {}

    const safeMeta = contentConfig.slidesMeta || {};

    bannerSlides =
      activeBanner.files
        ?.filter((file) => file.mimetype?.startsWith("image") || file.mimetype?.startsWith("video"))
        .map((file) => {
          const sm = safeMeta[file.id] || {};
          const pickLocalized = (val) => {
            if (!val) return "";
            if (typeof val === "string") return val;
            return val[t.langCode] || val.en || "";
          };
          return {
            id: file.id,
            source: `${API_BASE_URL}/files/${file.id}`,
            mimetype: file.mimetype,
            title: pickLocalized(sm.title),
            targetUrl: sm.targetUrl || "",
            buttonText: pickLocalized(sm.buttonText),
            lang: sm.lang || null,
          };
        }) || [];
        
    bannerSlides = bannerSlides.filter((s) => !s.lang || s.lang === t.langCode);
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Active Banner Carousel */}
        {bannerSlides.length > 0 && (
          <Carousel
            slideSize="100%"
            slideGap={{ base: "xs", sm: "md" }}
            emblaOptions={{ align: "start", loop: true }}
            withIndicators
          >
            {bannerSlides.map((slide) => (
              <Carousel.Slide key={slide.id} style={{ height: "auto" }}>
                {slide.targetUrl ? (
                  <Link to={slide.targetUrl} style={{ textDecoration: "none" }}>
                    <BannerCard {...slide} t={t} />
                  </Link>
                ) : (
                  <BannerCard {...slide} t={t} />
                )}
              </Carousel.Slide>
            ))}
          </Carousel>
        )}

        {/* Title Section */}
        <Title order={1} ta="center" fz={{ base: "24px", sm: "32px" }} fw={800}>
          {t("featuredProducts", "Popular Products")}
        </Title>

        <section aria-label={t("featuredProducts", "Popular Products")}>
          {loading && (
            <p className="sr-only" role="status" aria-live="polite">
              {t("loadingProducts", "Loading popular products…")}
            </p>
          )}
          {/* Products Grid */}
          {loading ? (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} height={400} radius="md" />
              ))}
            </SimpleGrid>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </SimpleGrid>
          )}
        </section>

        {/* Call to Action Buttons */}
        <Center mt="xl">
          <Group gap="md">
            <Button
              component={Link}
              to="/products"
              size="lg"
              radius="xl"
              leftSection={<Icons.IconShoppingBag size={20} aria-hidden="true" />}
            >
              {t("browseProducts", "Browse Products")}
            </Button>

            <Button
              component={Link}
              to="/about"
              variant="outline"
              color="gray"
              size="lg"
              radius="xl"
            >
              {t("learnMore", "About Us")}
            </Button>
          </Group>
        </Center>
      </Stack>
    </Container>
  );
}